import {
  differenceInDays,
  differenceInYears,
  endOfDay,
  isToday,
  startOfYear
} from "date-fns";

/** Matches useKivunovaTranslation `t` (i18next options for interpolation). */
export type MetricPeriodTranslateFn = (
  key: string,
  defaultValue: string,
  options?: Record<string, unknown>
) => string;

const passthroughTr: MetricPeriodTranslateFn = (_key, defaultValue) =>
  defaultValue;

export const getMetricPeriodLabel = (
  start: Date,
  end: Date,
  tr: MetricPeriodTranslateFn = passthroughTr
): string => {
  const today = endOfDay(new Date());
  const totalDays = differenceInDays(end, start);
  const totalYears = differenceInYears(end, start);

  // === Common quick ranges ===
  if (isToday(start) && isToday(end))
    return tr("dashboard-period-today", "today");
  if (totalDays === 0) return tr("dashboard-period-one-day", "1 day");
  if (totalDays === 6) return tr("dashboard-period-last-7-days", "last 7 days");
  if (totalDays === 29)
    return tr("dashboard-period-last-30-days", "last 30 days");
  if (start.getTime() === startOfYear(today).getTime() && isToday(end))
    return tr("dashboard-period-ytd", "YTD");
  if (totalYears === 1 && isToday(end))
    return tr("dashboard-period-last-12-months", "last 12 months");
  if (totalYears === 5 && isToday(end))
    return tr("dashboard-period-last-5-years", "last 5 years");

  // === Custom Range Duration ===
  const days = totalDays;
  const months = days / 30.44;
  const years = days / 365.25;

  let label: string;

  if (days < 30) {
    label =
      days === 1
        ? tr("dashboard-period-one-day", "1 day")
        : tr("dashboard-period-n-days", "{{count}} days", { count: days });
  } else if (days < 365) {
    const m = parseFloat(months.toFixed(1));
    const unit =
      months > 1
        ? tr("dashboard-period-unit-months", "months")
        : tr("dashboard-period-unit-month", "month");
    label = `${m} ${unit}`;
  } else {
    const y = parseFloat(years.toFixed(1));
    const unit =
      years > 1
        ? tr("dashboard-period-unit-years", "years")
        : tr("dashboard-period-unit-year", "year");
    label = `${y} ${unit}`;
  }

  return label;
};
